import { useSyncExternalStore } from "react";

/**
 * The Energy Advisor module's selected client — persistent module state.
 *
 * **Why this exists (and why it came back).** An earlier version of the
 * advisor nav remembered the last client and used it as a *fallback* for
 * rendering the client's tools. That was wrong: the tools then appeared while
 * you were on the picker choosing a client, and on `/customers` / `/accounts`
 * / `/deals`, which have nothing to do with that client. It was removed.
 *
 * The model is different now. The client is chosen in a **picker in the
 * sidebar header** (`AdvisorClientPicker`) — a peer of the module switcher,
 * one scope down — and that selection is deliberately meant to outlive the
 * route: the whole point is that navigating to Accounts, Clients, or Deals
 * keeps your client in scope, and the picker is how you change it. So the
 * selection is real module state, not a stale echo of a route you left.
 *
 * What makes this safe is that the picker *names* the scope. The client's
 * tool rows render beneath it and persist across shared records too, which
 * reads as "here are this client's tools" rather than "you are on one of
 * them" — the claim a section header used to make, and the reason an earlier
 * version scoped the rows to the URL instead.
 *
 * Session-scoped rather than server-side on purpose: it's a UI convenience,
 * not authorization. Every client-scoped route keeps its own permission and
 * feature gates regardless of what's remembered here.
 */

const STORAGE_KEY = "powerup:advisor-client";

const listeners = new Set<() => void>();

function read(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked storage — losing the remembered client is a far
    // smaller failure than a sidebar that won't render.
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/**
 * Remembers `id` as the client in scope. Called when the picker selects one
 * and when a client route renders (so a deep link adopts that client).
 *
 * Pass `null` to clear — used when the remembered client stops resolving
 * (deleted, or access revoked), so the picker doesn't keep showing a name
 * whose links 404.
 */
export function setAdvisorClient(id: string | null): void {
  if (read() === id) return;
  if (typeof window !== "undefined") {
    try {
      if (id === null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore — see `read` */
    }
  }
  for (const l of listeners) l();
}

/** Reactive read of the client in scope, or `null` if none is chosen yet. */
export function useAdvisorClientId(): string | null {
  return useSyncExternalStore(subscribe, read, () => null);
}
